import log from 'loglevel';

import {
  parseSuggestionTabAlertsState,
  type SuggestionTabAlertsState,
} from './suggestionTabAlertsState';
import {
  SUGGESTION_TAB_ALERTS_KEY_SEPARATOR,
  SUGGESTION_TAB_ALERTS_LOCK_PREFIX,
  SUGGESTION_TAB_ALERTS_STORAGE_KEY_PREFIX,
} from '../../utils/constants/suggestionTabAlerts';

export type SuggestionTabAlertsMutation = {
  next: SuggestionTabAlertsState;
  effect?: () => void;
};

const memoryFallback = new Map<string, SuggestionTabAlertsState>();

export function buildSuggestionTabAlertsKey(userId: string): string {
  return [SUGGESTION_TAB_ALERTS_STORAGE_KEY_PREFIX, userId].join(
    SUGGESTION_TAB_ALERTS_KEY_SEPARATOR,
  );
}

export function buildSuggestionTabAlertsLockName(userId: string): string {
  return [SUGGESTION_TAB_ALERTS_LOCK_PREFIX, userId].join(
    SUGGESTION_TAB_ALERTS_KEY_SEPARATOR,
  );
}

function switchToMemory(
  key: string,
  err: unknown,
  state: SuggestionTabAlertsState,
): void {
  log.error('suggestionTabAlertsStorage: localStorage unavailable', err);
  memoryFallback.set(key, state);
}

export function readSuggestionTabAlertsState(
  key: string,
): SuggestionTabAlertsState {
  const inMemory = memoryFallback.get(key);
  if (inMemory) return inMemory;
  try {
    return parseSuggestionTabAlertsState(window.localStorage.getItem(key));
  } catch (err) {
    const empty = parseSuggestionTabAlertsState(null);
    switchToMemory(key, err, empty);
    return empty;
  }
}

function writeSuggestionTabAlertsState(
  key: string,
  state: SuggestionTabAlertsState,
): void {
  if (memoryFallback.has(key)) {
    memoryFallback.set(key, state);
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch (err) {
    switchToMemory(key, err, state);
  }
}

function applyMutation(
  key: string,
  mutate: (state: SuggestionTabAlertsState) => SuggestionTabAlertsMutation,
): SuggestionTabAlertsState {
  const state = readSuggestionTabAlertsState(key);
  const { next, effect } = mutate(state);
  if (next !== state) writeSuggestionTabAlertsState(key, next);
  effect?.();
  return next;
}

export async function updateSuggestionTabAlertsState(
  userId: string,
  mutate: (state: SuggestionTabAlertsState) => SuggestionTabAlertsMutation,
): Promise<SuggestionTabAlertsState> {
  const key = buildSuggestionTabAlertsKey(userId);
  try {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return await navigator.locks.request(
        buildSuggestionTabAlertsLockName(userId),
        async () => applyMutation(key, mutate),
      );
    }
    return applyMutation(key, mutate);
  } catch (err) {
    log.error('updateSuggestionTabAlertsState', err);
    return readSuggestionTabAlertsState(key);
  }
}
